import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode
} from "react";
import { useAuth } from "../auth/AuthProvider.js";
import { Badge, Button, Card, Field } from "../components/index.js";
import {
  settingsToForm,
  validateSettingsForm,
  type SettingsFormErrors,
  type SettingsFormValues
} from "../lib/settings-form.js";
import type { LabelFormat } from "../types.js";

/**
 * Configurações (SDD §10.2/§11.7 — singleton ADMIN-only). Acessível pelo ícone
 * de engrenagem no cabeçalho (não consta no menu §4.1); a rota é guardada por
 * RequireRole. GET devolve defaults (`isDefault`) enquanto nada foi salvo.
 */
const EMPTY_FORM: SettingsFormValues = {
  activeMarketplaceId: "",
  defaultPrepCenterName: "",
  lowStockThresholdDays: "",
  leadTimeTargetDays: "",
  inventorySyncEnabled: true,
  labelFormat: "THERMAL",
  amazonSellerId: "",
  sourceAddressName: "",
  sourceAddressLine1: "",
  sourceAddressCity: "",
  sourceAddressPostalCode: "",
  sourceAddressCountryCode: "GB",
  sourceAddressPhoneNumber: ""
};

// Chaves de texto obrigatórias da origem do envio (todas string em Values+Errors).
type SourceFieldKey =
  | "amazonSellerId"
  | "sourceAddressName"
  | "sourceAddressLine1"
  | "sourceAddressCity"
  | "sourceAddressPostalCode"
  | "sourceAddressCountryCode"
  | "sourceAddressPhoneNumber";

export function ConfiguracoesPage(): ReactNode {
  const { api } = useAuth();
  const [values, setValues] = useState<SettingsFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<SettingsFormErrors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getSettings();
      setValues(settingsToForm(res.settings));
      setIsDefault(res.isDefault);
    } catch (error: unknown) {
      setLoadError(
        error instanceof Error ? error.message : "Falha ao carregar as configurações."
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  function update<K extends keyof SettingsFormValues>(
    key: K,
    value: SettingsFormValues[K]
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSuccess(false);
  }

  // Campo de texto obrigatório da origem do envio (reduz boilerplate dos 7 inputs).
  function sourceField(
    key: SourceFieldKey,
    label: string,
    opts: {
      hint?: string;
      placeholder?: string;
      mono?: boolean;
      style?: CSSProperties;
    } = {}
  ): ReactNode {
    const error = errors[key];
    return (
      <Field
        label={label}
        htmlFor={`cfg-${key}`}
        required
        {...(opts.hint !== undefined ? { hint: opts.hint } : {})}
        {...(opts.style !== undefined ? { style: opts.style } : {})}
        {...(error !== undefined ? { error } : {})}
      >
        <input
          id={`cfg-${key}`}
          className={opts.mono === true ? "mono" : undefined}
          value={values[key]}
          placeholder={opts.placeholder}
          disabled={loading}
          onChange={(e) => update(key, e.target.value)}
        />
      </Field>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveError(null);
    setSuccess(false);
    const validation = validateSettingsForm(values);
    setErrors(validation.errors);
    if (!validation.valid || validation.input === null) {
      return;
    }
    setSaving(true);
    try {
      const res = await api.putSettings(validation.input);
      setValues(settingsToForm(res.settings));
      setIsDefault(false);
      setSuccess(true);
    } catch (error: unknown) {
      setSaveError(
        error instanceof Error ? error.message : "Falha ao salvar as configurações."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header>
        <h2 style={{ fontSize: 21, fontWeight: 700 }}>Configurações</h2>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
          Parâmetros da aplicação · acesso restrito (Admin)
        </p>
      </header>

      <Card style={{ maxWidth: 560 }}>
        {loadError !== null ? (
          <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5 }}>
            {loadError}
          </p>
        ) : null}

        {isDefault ? (
          <div style={{ marginBottom: "0.85rem" }}>
            <Badge tone="amber">Valores padrão</Badge>{" "}
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Nenhuma configuração salva ainda — exibindo os padrões.
            </span>
          </div>
        ) : null}

        <form
          onSubmit={handleSubmit}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
        >
          <Field
            label="Marketplace ativo"
            htmlFor="cfg-marketplace"
            required
            hint="ID do marketplace Amazon (ex.: UK A1F83G8C2ARO7P)."
            {...(errors.activeMarketplaceId !== undefined
              ? { error: errors.activeMarketplaceId }
              : {})}
          >
            <input
              id="cfg-marketplace"
              className="mono"
              value={values.activeMarketplaceId}
              disabled={loading}
              onChange={(e) => update("activeMarketplaceId", e.target.value)}
            />
          </Field>

          <Field
            label="Prep Center padrão"
            htmlFor="cfg-prepcenter"
            required
            {...(errors.defaultPrepCenterName !== undefined
              ? { error: errors.defaultPrepCenterName }
              : {})}
          >
            <input
              id="cfg-prepcenter"
              value={values.defaultPrepCenterName}
              disabled={loading}
              onChange={(e) => update("defaultPrepCenterName", e.target.value)}
            />
          </Field>

          <div style={{ display: "flex", gap: "0.85rem", flexWrap: "wrap" }}>
            <Field
              label="Threshold de estoque baixo (dias)"
              htmlFor="cfg-lowstock"
              required
              style={{ flex: "1 1 200px" }}
              {...(errors.lowStockThresholdDays !== undefined
                ? { error: errors.lowStockThresholdDays }
                : {})}
            >
              <input
                id="cfg-lowstock"
                type="number"
                inputMode="numeric"
                value={values.lowStockThresholdDays}
                disabled={loading}
                onChange={(e) => update("lowStockThresholdDays", e.target.value)}
              />
            </Field>

            <Field
              label="Lead time alvo (dias)"
              htmlFor="cfg-leadtime"
              required
              style={{ flex: "1 1 200px" }}
              {...(errors.leadTimeTargetDays !== undefined
                ? { error: errors.leadTimeTargetDays }
                : {})}
            >
              <input
                id="cfg-leadtime"
                type="number"
                inputMode="numeric"
                value={values.leadTimeTargetDays}
                disabled={loading}
                onChange={(e) => update("leadTimeTargetDays", e.target.value)}
              />
            </Field>
          </div>

          <Field label="Formato de etiqueta padrão" htmlFor="cfg-labelformat">
            <select
              id="cfg-labelformat"
              value={values.labelFormat}
              disabled={loading}
              onChange={(e) => update("labelFormat", e.target.value as LabelFormat)}
            >
              <option value="THERMAL">Térmica (50×30 mm — perfil Bristol)</option>
              <option value="A4">PDF A4</option>
            </select>
          </Field>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: 13,
              cursor: "pointer"
            }}
          >
            <input
              type="checkbox"
              checked={values.inventorySyncEnabled}
              disabled={loading}
              onChange={(e) => update("inventorySyncEnabled", e.target.checked)}
              style={{ width: "auto" }}
            />
            Sincronização automática do inventário FBA
          </label>

          <fieldset
            style={{
              border: "1px solid var(--border-soft)",
              borderRadius: 9,
              padding: "0.85rem",
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.85rem"
            }}
          >
            <legend style={{ fontSize: 12, fontWeight: 700, padding: "0 0.4rem" }}>
              Origem do envio (Amazon)
            </legend>
            <p style={{ fontSize: 11.5, color: "var(--text-muted)", margin: 0 }}>
              Seller ID e endereço de origem usados pela Amazon ao registrar o
              plano. Preenchidos uma vez aqui — o botão "Criar envios" os usa
              automaticamente, sem pedir nada no wizard.
            </p>
            {sourceField("amazonSellerId", "Seller ID (Amazon)", {
              placeholder: "ex.: A2EUQ1WTGCTBG2",
              mono: true
            })}
            {sourceField("sourceAddressName", "Nome do endereço")}
            {sourceField("sourceAddressLine1", "Endereço (linha 1)")}
            <div style={{ display: "flex", gap: "0.85rem", flexWrap: "wrap" }}>
              {sourceField("sourceAddressCity", "Cidade", {
                style: { flex: "1 1 160px" }
              })}
              {sourceField("sourceAddressPostalCode", "Código postal", {
                style: { flex: "1 1 120px" }
              })}
              {sourceField("sourceAddressCountryCode", "País", {
                style: { flex: "0 1 90px" }
              })}
            </div>
            {sourceField("sourceAddressPhoneNumber", "Telefone", {
              placeholder: "ex.: +441179000000",
              hint: "Exigido pela Amazon ao registrar o envio."
            })}
          </fieldset>

          {saveError !== null ? (
            <p role="alert" style={{ color: "var(--orange)", fontSize: 12, fontWeight: 600 }}>
              {saveError}
            </p>
          ) : null}
          {success ? (
            <p role="status" style={{ color: "var(--green)", fontSize: 12, fontWeight: 600 }}>
              ✓ Configurações salvas.
            </p>
          ) : null}

          <div>
            <Button type="submit" disabled={loading || saving}>
              {saving ? "Salvando…" : "Salvar configurações"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
